/**
 * Contact Cache - In-memory cache with TTL for contact lookups
 * 
 * Reduces API calls during incoming calls by caching contact information.
 * Cache is invalidated via Socket.IO events when contacts are updated.
 */

// Default TTL: 60 seconds
const DEFAULT_TTL = 60000;

class ContactCache {
  constructor(ttl = DEFAULT_TTL) {
    this.cache = new Map();
    this.ttl = ttl;
    this.hits = 0;
    this.misses = 0;
  }

  /**
   * Get contact from cache
   * @param {string} phone - Phone number to lookup
   * @returns {Object|null} Cached contact data or null if not found/expired
   */
  get(phone) {
    const normalizedPhone = this._normalizePhone(phone);
    const entry = this.cache.get(normalizedPhone);
    
    if (!entry) {
      this.misses++;
      return null;
    }
    
    // Check if entry has expired
    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(normalizedPhone);
      this.misses++;
      return null;
    }
    
    this.hits++;
    return entry.data;
  }

  /**
   * Set contact in cache
   * @param {string} phone - Phone number
   * @param {Object} data - Contact data to cache
   */
  set(phone, data) {
    const normalizedPhone = this._normalizePhone(phone);
    this.cache.set(normalizedPhone, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Check if phone exists in cache (without updating hit/miss stats)
   * @param {string} phone - Phone number
   * @returns {boolean}
   */
  has(phone) {
    const normalizedPhone = this._normalizePhone(phone);
    const entry = this.cache.get(normalizedPhone);
    
    if (!entry) return false;
    
    // Check expiration
    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(normalizedPhone);
      return false;
    }
    
    return true;
  }

  /**
   * Invalidate (remove) a specific contact from cache
   * Called when contact is updated via Socket.IO
   * @param {string} phone - Phone number to invalidate
   */
  invalidate(phone) {
    const normalizedPhone = this._normalizePhone(phone);
    this.cache.delete(normalizedPhone);
  }

  /**
   * Invalidate contact by ID
   * Searches through cache to find and remove by contact ID
   * @param {number|string} contactId - Contact ID to invalidate
   */
  invalidateById(contactId) {
    for (const [phone, entry] of this.cache.entries()) {
      if (entry.data?.contact?.id === contactId) {
        this.cache.delete(phone);
        return;
      }
    }
  }

  /**
   * Clear all cached entries
   */
  clear() {
    this.cache.clear();
    this.hits = 0;
    this.misses = 0;
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache stats including size, hit rate, etc.
   */
  getStats() {
    const total = this.hits + this.misses;
    const hitRate = total > 0 ? (this.hits / total * 100).toFixed(1) : 0;
    
    return {
      size: this.cache.size,
      hits: this.hits,
      misses: this.misses,
      hitRate: `${hitRate}%`,
      ttl: this.ttl,
    };
  }

  /**
   * Normalize phone number for consistent cache keys
   * Removes non-numeric characters except +
   * @param {string} phone - Phone number
   * @returns {string} Normalized phone number
   */
  _normalizePhone(phone) {
    if (!phone) return '';
    return phone.replace(/[^0-9+]/g, '');
  }

  /**
   * Clean expired entries (garbage collection)
   * Can be called periodically to free memory
   */
  cleanup() {
    const now = Date.now();
    let cleaned = 0;
    
    for (const [phone, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.ttl) {
        this.cache.delete(phone);
        cleaned++;
      }
    }
    
    if (cleaned > 0) {
      console.log(`[ContactCache] Cleaned ${cleaned} expired entries`);
    }
    
    return cleaned;
  }
}

// Export singleton instance
const contactCache = new ContactCache();
export default contactCache;
export { ContactCache };

